"use client";

import { useState } from "react";
import Link from "next/link";
import NameSearchTrigger from "./name-search-trigger";

interface NavLink {
  href: string;
  label: string;
  highlight?: boolean;
}

interface MobileNavProps {
  links: NavLink[];
  userHref?: string;
  signOutAction: () => Promise<void>;
  isLoggedIn: boolean;
}

export default function MobileNav({ links, userHref, signOutAction, isLoggedIn }: MobileNavProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Hamburger button — only visible on small screens */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="sm:hidden flex flex-col justify-center items-center w-9 h-9 rounded-lg hover:bg-purple-700 transition gap-1.5 select-none"
        style={{ WebkitUserSelect: "none", userSelect: "none" }}
        aria-label="Toggle menu"
        aria-expanded={open}
      >
        {/* Animated bars — becomes X when open */}
        <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${open ? "rotate-45 translate-y-2" : ""}`} />
        <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${open ? "opacity-0 scale-x-0" : ""}`} />
        <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${open ? "-rotate-45 -translate-y-2" : ""}`} />
      </button>

      {/* Dropdown menu */}
      {open && (
        <>
          {/* Backdrop — tap outside to close */}
          <div
            className="sm:hidden fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />

          {/* Menu panel */}
          <div className="sm:hidden absolute top-full left-0 right-0 z-50 bg-purple-900 border-t border-purple-700/50 shadow-2xl">
            <div className="flex flex-col py-2">

              {/* Search */}
              <div className="px-6 py-1">
                <NameSearchTrigger className="w-full text-left px-0 py-2 text-sm font-semibold text-purple-200 hover:text-white transition select-none" />
              </div>

              {/* Nav links */}
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className={`px-6 py-3 text-sm font-semibold transition hover:bg-purple-700 select-none ${
                    link.highlight ? "text-yellow-300" : "text-purple-200 hover:text-white"
                  }`}
                  style={{ WebkitUserSelect: "none", userSelect: "none" }}
                >
                  {link.label}
                </Link>
              ))}

              {/* Divider */}
              <div className="mx-6 my-2 h-px bg-purple-700/50" />

              {/* Settings + sign out */}
              {isLoggedIn ? (
                <>
                  {userHref && (
                    <Link
                      href={userHref}
                      onClick={() => setOpen(false)}
                      className="px-6 py-3 text-sm font-semibold text-purple-200 hover:text-white hover:bg-purple-700 transition select-none"
                      style={{ WebkitUserSelect: "none", userSelect: "none" }}
                    >
                      ⚙️ Settings
                    </Link>
                  )}
                  <form action={signOutAction}>
                    <button
                      type="submit"
                      className="w-full text-left px-6 py-3 text-sm font-bold text-purple-200 hover:text-white hover:bg-purple-700 transition select-none"
                      style={{ WebkitUserSelect: "none", userSelect: "none" }}
                    >
                      Sign Out
                    </button>
                  </form>
                </>
              ) : (
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="px-6 py-3 text-sm font-bold text-white hover:bg-purple-700 transition select-none"
                  style={{ WebkitUserSelect: "none", userSelect: "none" }}
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
